/**
 * Where the other person's voice comes out.
 *
 * A `MediaStream` is not audible on its own — it is audible when a media element
 * is playing it, and nowhere else. That element used to be the overlay's
 * `<video>`, which exists only while a video call is on screen: a voice call
 * rendered no element at all, and minimising a video call unmounted the one it
 * had. Chrome hid the mistake by rendering remote audio tracks without being
 * asked; WebKit does not, so on an iPhone those two cases were silence with a
 * running timer.
 *
 * Hence this: one element, mounted by CallProvider for the whole life of the
 * app, holding whatever the current call's remote stream is. **It must stay
 * outside every phase, `minimized` and `call.video` condition** — that is the
 * entire point of it, and moving it back inside the overlay restores the bug in
 * exactly the two places nobody tests.
 *
 * The overlay's `<video>` is muted for the same reason: the picture is its job,
 * the sound is this one's, and both elements holding the same stream unmuted
 * would play the far end twice.
 */
import { useEffect, useRef } from 'react';
import type { CallNote } from '../lib/callDiagnostics.ts';

/**
 * How many times a refused playback is asked for again before this gives up.
 * Bounded because every trigger below can in principle fire more than once, and
 * an element that will never play must not become a loop of rejections.
 */
const MAX_ATTEMPTS = 5;

/**
 * When the timed retries run, counted from the first refusal. The iPhone app
 * files an AbortError a few seconds into every call, right where the remote
 * stream arrives *and* CallAudioPlugin takes the AVAudioSession over — if that
 * is a race, a nudge after the session has settled is what wins it, and which
 * delay won is written into the recovery row.
 */
const RETRY_DELAYS_MS = [1000, 3000];

export function RemoteAudio({
  stream,
  onDiagnostic,
}: {
  stream: MediaStream | null;
  onDiagnostic: (note: CallNote) => void;
}): React.ReactElement {
  const element = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = element.current;
    if (!audio) return;
    // A property, not an attribute — the same reason the overlay binds by ref.
    audio.srcObject = stream;
    if (!stream) return;

    let disposed = false;
    let attempts = 0;
    /** The first refusal's name; non-null is what arms every retry below. */
    let refused: string | null = null;
    const timers: number[] = [];
    const unhooks: (() => void)[] = [];

    // What the stream held at the moment playback was asked for. The refusal
    // arrives as a bare error name, and whether there was an audio track to
    // play — and whether RTP had reached it yet — is the difference between an
    // autoplay problem and a media-path problem when the row is read later.
    const census = () => {
      const tracks = stream.getAudioTracks();
      const muted = tracks.filter((track) => track.muted).length;
      return `audio tracks ${tracks.length}${muted > 0 ? `, ${muted} muted` : ''}`;
    };

    const tryPlay = (trigger: string) => {
      if (disposed || attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      // `autoPlay` covers the ordinary case, but a browser that refuses it does
      // so silently, and the shape of that failure is identical to the one this
      // file exists to fix: a call that connects, counts seconds and says
      // nothing. So the play is asked for explicitly and its refusal is written
      // down.
      audio.play().then(
        () => {
          if (disposed || refused === null) return;
          // A recovery is a row of its own: it says the refusal was a race
          // rather than a policy, and it names the nudge that won it.
          onDiagnostic({
            detail: 'call/audio/recovered',
            message: `remote audio recovered · ${trigger} · attempt ${attempts} · ${census()}`,
          });
          refused = null;
        },
        (error: unknown) => {
          const name = error instanceof Error ? error.name : 'unknown';
          if (refused === null) {
            onDiagnostic({
              detail: 'call/audio/play-blocked',
              message: `remote audio would not play · ${name} · ${census()}`,
            });
            for (const delay of RETRY_DELAYS_MS) {
              timers.push(window.setTimeout(() => retry(`retry ${delay}ms`), delay));
            }
          }
          refused = name;
        },
      );
    };

    /** A nudge, not an opening move: does nothing unless a refusal is standing. */
    const retry = (trigger: string) => {
      if (refused !== null) tryPlay(trigger);
    };

    const watchTrack = (track: MediaStreamTrack) => {
      // A remote track is born muted and unmutes when its RTP arrives; that
      // moment is the first at which playing it could sound like anything.
      const onUnmute = () => retry('unmute');
      track.addEventListener('unmute', onUnmute);
      unhooks.push(() => track.removeEventListener('unmute', onUnmute));
    };

    // PeerCall announces the remote stream on its *first* track event, which
    // can be the video track — so the first play here may be asked of a stream
    // with no sound in it yet. The audio arriving is another retry moment.
    const onAddTrack = (event: MediaStreamTrackEvent) => {
      if (event.track.kind === 'audio') watchTrack(event.track);
      retry('addtrack');
    };
    stream.addEventListener('addtrack', onAddTrack);
    unhooks.push(() => stream.removeEventListener('addtrack', onAddTrack));
    for (const track of stream.getAudioTracks()) watchTrack(track);

    tryPlay('bind');

    return () => {
      disposed = true;
      for (const timer of timers) window.clearTimeout(timer);
      for (const unhook of unhooks) unhook();
    };
  }, [onDiagnostic, stream]);

  // No `controls`, so this renders nothing and needs no styling.
  return <audio ref={element} autoPlay />;
}
